import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  FormGroupDirective,
  NgForm,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { ErrorStateMatcher, ThemePalette } from '@angular/material/core';
import { CustomAnimation } from '../custom-animation';
import { AuthService, complexPasswordValidator, EncryptedContactIdRouteInterceptor, GuestUser } from '@aiwyn/auth';
import { AssetUriService } from '@aiwyn/config';
import { Router } from '@angular/router';
import { LegalService } from '../../../../services/legal.service';
import { LoadableHelper, LoadableStatus, Logger } from '@aiwyn/shared';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { ModalAlertComponent } from '../../../main/modals/alert/modal-alert.component';
import { MatDialog } from '@angular/material/dialog';

export class RegisterErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const invalidParent = !!(
      control
      && control.parent
      && control.parent.invalid
      && control.parent.dirty
      && control.parent.hasError('passwordsNotMatching'));
    return (invalidParent);
  }
}

const matchPasswordValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.parent) return null; // Ensure parent (FormGroup) exists

  const password = control.parent.get('password')?.value;
  const confirmPassword = control.value;

  return password === confirmPassword ? null : { passwordsNotMatching: true };
};

@Component({
  selector: 'app-register-component',
  templateUrl: './app-register.component.html',
  styleUrls: ['./app-register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: CustomAnimation,
  standalone: false,
})
export class AppRegisterComponent implements OnInit, OnDestroy {

  @Input() logoUrl: string;
  @Input() appearance: MatFormFieldAppearance;
  @Input() tosUrl: string;
  @Input() privacyPolicyUrl: string;

  // i18n common
  @Input() titleText = 'CREATE AN ACCOUNT';
  @Input() readAncAcceptText = 'I read and accept';
  @Input() termsAndConditionsText = 'terms and conditions';
  @Input() createAccountButtonText = 'CREATE AN ACCOUNT';
  @Input() alreadyHaveAccountText = 'Already have an account?';
  @Input() loginButtonText = 'Log In';

  // i18n name and email
  @Input() nameText = 'Name';
  @Input() nameErrorRequiredText = 'Name is required';

  // i18n email
  @Input() emailText = 'Email';
  @Input() emailErrorRequiredText = 'Email is required';
  @Input() emailErrorPatternText = 'Please enter a valid email address';

  // i18n password
  @Input() passwordText = 'Password';
  @Input() passwordErrorRequiredText = 'Password is required';
  @Input() passwordConfirmationText = 'Password Confirmation';
  @Input() passwordConfirmationErrorRequiredText = 'Password confirmation is required';
  @Input() passwordErrorMatchText = 'Password must match';

  readonly specialCharacters = '!@#$%^&*()';

  registerForm: UntypedFormGroup;
  authenticationError = false;
  matcher = new RegisterErrorStateMatcher();

  private readonly _unsubscribe$ = new Subject<void>();
  private readonly _registrationRequest$: BehaviorSubject<LoadableStatus> = new BehaviorSubject<LoadableStatus>(LoadableStatus.DEFAULT);
  isRegistrationLoading$: Observable<boolean> = this._registrationRequest$.pipe(
    map(status => status === LoadableStatus.LOADING),
  );

  constructor(
    private formBuilder: UntypedFormBuilder,
    public authService: AuthService,
    private router: Router,
    public assetsUriService: AssetUriService,
    public readonly legalService: LegalService,
    private readonly logger: Logger,
    private readonly encryptedContactIdGuard: EncryptedContactIdRouteInterceptor,
    private readonly dialog: MatDialog,
  ) { }

  get color(): string | ThemePalette {
    return this.authenticationError ? 'warn' : 'primary';
  }

  get colorAccent(): string | ThemePalette {
    return this.authenticationError ? 'warn' : 'accent';
  }

  ngOnInit(): void {
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
      email: ['', [Validators.required, Validators.email]],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(12),
          Validators.maxLength(60),
          // Validate that the password does not contain leading or trailing whitespace
          (control: AbstractControl) => {
            const controlValue = control?.value as string;
            if (!controlValue || !controlValue.includes('')) {
              return null;
            }

            return controlValue.trim().length !== controlValue.length ? { whitespace: true } : null;
          },
          complexPasswordValidator
        ]
      ],
      passwordConfirm: ['', [Validators.required, matchPasswordValidator]],
      terms: [false, Validators.requiredTrue],
    });

    // Update the validity of the 'passwordConfirm' field
    // when the 'password' field changes
    this.registerForm.controls.password.valueChanges.pipe(
      takeUntil(this._unsubscribe$),
    ).subscribe(() => {
      this.registerForm.controls.passwordConfirm.updateValueAndValidity();
    });

    // Prepopulate the email for the form based on the user if possible.
    this.encryptedContactIdGuard.getDecryptedContactId().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      take(1),
      switchMap(({ status, data }) => {
        if (status === LoadableStatus.ERROR || !data?.email) {
          return this.authService.getUser().pipe(
            take(1),
            withLatestFrom(this.authService.getAuthEmail()),
            tap(([user, authEmail]) => {
              const encryptedEmailList = user instanceof GuestUser ? user.getEncryptedEmailList() : [];

              let email = '';
              if (encryptedEmailList.length === 1) {
                email = encryptedEmailList[0];
              } else if (authEmail) {
                email = authEmail;
              }

              this.registerForm.get('email').setValue(email);
            }),
          )
        }

        this.registerForm.get('email').setValue(data.email || '');
        this.registerForm.get('email').disable();
        return EMPTY;
      }),
    ).subscribe();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  createAccount(): void {
    if (this._registrationRequest$.value === LoadableStatus.LOADING) {
      this.logger.info('AUTH :: AppRegisterComponent :: createAccount :: registration already underway, skipping');
      return;
    }

    this._registrationRequest$.next(LoadableStatus.LOADING);

    this.logger.info('AUTH :: AppRegisterComponent :: createAccount :: Starting account registration');
    this.authenticationError = false;

    this.authService.getAuthProviderToken().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      take(1),
      switchMap(({ status, data: token }) => {
        if (status === LoadableStatus.ERROR) {
          throw new Error('Failed to retrieve auth token from provider');
        }

        if (!token) {
          this.logger.info('AUTH :: AppRegisterComponent :: createAccount :: Registering a new user');
          return this.authService.registerNewUser(
            this.registerForm.controls.email.value,
            this.registerForm.controls.password.value,
            this.registerForm.controls.name.value
          );
        }

        this.logger.info('AUTH :: AppRegisterComponent :: createAccount :: Registering new account from a guest user');
        return this.authService.registerGuestUser(
          this.registerForm.controls.email.value,
          this.registerForm.controls.password.value,
          this.registerForm.controls.name.value
        );
      }),
      take(1),
      tap({
        next: _ => {
          this.logger.info('AUTH :: AppRegisterComponent :: createAccount :: completed new user registration');
          this._registrationRequest$.next(LoadableStatus.LOADED);
        },
        error: error => {
          this.authenticationError = true;
          this._registrationRequest$.next(LoadableStatus.ERROR);
          this.logger.warn('AUTH :: AppRegisterComponent :: createAccount :: Failed to register new user', error);

          this.dialog.open(
            ModalAlertComponent,
            {
              disableClose: true,
              data: {
                title: 'Unable to Create Account',
                message: 'Check your email for any confirmation links or contact support for assistance.',
              }
            }
          );
        },
      }),
    ).subscribe();
  }

  navigateToLogin(): void {
    this.authService.signOut().subscribe();
  }
}
